import React, { useState, useEffect } from "react";
import ASSETS from "../assets.json";
import "./ValuePropsSection.scss";

const {
    imgFidelity:          IMG_FIDELITY,
    imgFidelityWoven:     IMG_FIDELITY_WOVEN,
    imgFidelityPattern:   IMG_FIDELITY_PATTERN,
    imgAccuracy:          IMG_ACCURACY,
    imgAccuracyNightstand: IMG_ACCURACY_NIGHTSTAND,
    imgControlDresser:    IMG_CONTROL_DRESSER,
    imgControlThumb1:     IMG_CONTROL_THUMB1,
    imgControlThumb2:     IMG_CONTROL_THUMB2,
    imgControlBase:       IMG_CONTROL_BASE,
    imgControlEdit:       IMG_CONTROL_EDIT,
    imgScaleDyson:        IMG_SCALE_DYSON,
    imgScaleDyson4:       IMG_SCALE_DYSON4,
    imgScaleDyson12:      IMG_SCALE_DYSON12,
    iconFidelity:         ICON_FIDELITY,
    iconAccuracy:         ICON_ACCURACY,
    iconControl:          ICON_CONTROL,
    iconScale:            ICON_SCALE,
    iconCubePlus:         ICON_CUBE_PLUS,
    iconSparkle:          ICON_SPARKLE,
    logoVeo4:             LOGO_VEO4,
    logoGemini:           LOGO_GEMINI,
    logoFlux:             LOGO_FLUX,
    logoBen2:             LOGO_BEN2,
    logoRunway:           LOGO_RUNWAY,
    logoTrellis:          LOGO_TRELLIS,
    logoWonder:           LOGO_WONDER,
} = ASSETS.valueProps;

// ─── Logo marquee data ────────────────────────────────────────────────────────
// Logos are duplicated for seamless infinite scroll.
// Translation = sum of (logo_w + gap) for one set — update lv2Marquee when changing.
const LOGO_SET = [
    { src: LOGO_VEO4,   alt: "Veo 4",  w: 116, h: 40 },
    { src: LOGO_GEMINI, alt: "Gemini", w: 108, h: 40 },
    { src: LOGO_FLUX,   alt: "FLUX.2", w: 130, h: 24 },
    { src: LOGO_BEN2,   alt: "BEN2",          w: 28, h: 28, label: "BEN2" },
    { src: LOGO_RUNWAY,  alt: "Gen 4.5 Runway", w: 28, h: 28, label: "Gen 4.5 (Runway)" },
    { src: LOGO_TRELLIS, alt: "Trellis 2.0",    w: 28, h: 28, label: "Trellis 2.0" },
    { src: LOGO_WONDER,  alt: "Wonder",          w: 28, h: 28, label: "Wonder" },
];
const LOGOS = [...LOGO_SET, ...LOGO_SET];

// ─── Value prop definitions ───────────────────────────────────────────────────
const PROPS = [
    {
        key:         "fidelity",
        label:       "Fidelity",
        icon:        ICON_FIDELITY,
        compactImg:  IMG_FIDELITY,
        compactCls:  "lv2-vp-img-fidelity",
        slideCount:  3,
        description: "Create high-fidelity product visuals with realistic textures and unmatched attention to detail – ready for ecommerce, ads, and campaigns.",
    },
    {
        key:         "accuracy",
        label:       "Accuracy",
        icon:        ICON_ACCURACY,
        compactImg:  IMG_ACCURACY,
        compactCls:  "lv2-vp-img-accuracy",
        slideCount:  2,
        description: "Keep your products true to life with visuals that reflect real dimensions, proportions, and details you can trust.",
    },
    {
        key:         "control",
        label:       "Control",
        icon:        ICON_CONTROL,
        compactImg:  IMG_CONTROL_EDIT,
        compactCls:  "lv2-vp-img-control",
        slideCount:  2,
        description: "Fine-tune every image with built-in editing tools, giving you full control over outputs instead of relying on unpredictable results.",
    },
    {
        key:         "scale",
        label:       "Scale",
        icon:        ICON_SCALE,
        compactImg:  IMG_SCALE_DYSON,
        compactCls:  "lv2-vp-img-scale",
        slideCount:  1,
        description: "Generate product visuals in bulk and apply consistent brand styles across every image, variation, and campaign.",
    },
];

// ─── Expanded slide content ───────────────────────────────────────────────────
// key prop triggers CSS animation restart on every slide change
function ExpandedSlide({ propKey, slideIdx }) {
    const animKey = `${propKey}-${slideIdx}`;

    if (propKey === "fidelity") {
        const slides = [
            { img: IMG_FIDELITY,         bubble: "Realistic glass texture" },
            { img: IMG_FIDELITY_WOVEN,   bubble: "Intricate woven textures" },
            { img: IMG_FIDELITY_PATTERN, bubble: "Distinctive patterns"     },
        ];
        const s = slides[slideIdx] || slides[0];
        return (
            <div key={animKey} className="lv2-vp-slide lv2-vp-slide-fadein">
                <img className="lv2-vp-slide-img lv2-vp-zoom" src={s.img} alt={s.bubble} />
                <div className="lv2-vp-bubble lv2-vp-bubble-fadein">{s.bubble}</div>
            </div>
        );
    }

    if (propKey === "accuracy") {
        if (slideIdx === 0) {
            return (
                <div key={animKey} className="lv2-vp-slide lv2-vp-slide-fadein">
                    <img className="lv2-vp-slide-img lv2-vp-zoom" src={IMG_ACCURACY} alt="Product visual with accurate real-world dimensions" />
                </div>
            );
        }
        return (
            <div key={animKey} className="lv2-vp-slide lv2-vp-slide-fadein">
                <img className="lv2-vp-slide-img lv2-vp-zoom-detail" src={IMG_ACCURACY} alt="Detailed view of product proportions" />
                <div className="lv2-vp-nightstand-card lv2-vp-bubble-fadein">
                    <div className="lv2-vp-nightstand-img-wrap">
                        <img src={IMG_ACCURACY_NIGHTSTAND} alt="Nightstand reference image" />
                    </div>
                    <div className="lv2-vp-nightstand-info">
                        <p className="manrope f-14 w-700 lh-22 black-14">Nightstand</p>
                        <p className="manrope f-14 w-400 lh-22 black-14">18.5"W × 16.5"D × 22.24"H</p>
                    </div>
                </div>
            </div>
        );
    }

    if (propKey === "control") {
        if (slideIdx === 0) {
            return (
                <div key={animKey} className="lv2-vp-slide lv2-vp-slide-fadein">
                    <img className="lv2-vp-slide-img lv2-vp-zoom" src={IMG_CONTROL_DRESSER} alt="Dresser scene for editing" />
                    <div className="lv2-vp-control-panel lv2-vp-bubble-fadein">
                        <div className="lv2-vp-control-thumbs">
                            <div className="lv2-vp-thumb-wrap">
                                <img src={IMG_CONTROL_THUMB1} alt="Scene variation option 1" />
                            </div>
                            <div className="lv2-vp-thumb-wrap">
                                <img src={IMG_CONTROL_THUMB2} alt="Scene variation option 2" />
                            </div>
                        </div>
                        <p className="manrope f-14 w-400 lh-22 black-14">Replace the artwork and rug</p>
                        <div className="lv2-vp-control-actions">
                            <div className="lv2-vp-add-prop-btn">
                                <img src={ICON_CUBE_PLUS} alt="" />
                                <span className="manrope f-14 w-500 black-14">Add prop</span>
                            </div>
                            <div className="lv2-vp-apply-btn">
                                <img src={ICON_SPARKLE} alt="" />
                                <span className="manrope f-14 w-700 white">Apply</span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div key={animKey} className="lv2-vp-slide lv2-vp-slide-fadein">
                <img className="lv2-vp-control-before" src={IMG_CONTROL_BASE} alt="Base image before editing" />
                <img className="lv2-vp-control-after"  src={IMG_CONTROL_EDIT} alt="Image after editing" />
                <div className="lv2-vp-tag lv2-vp-tag-left">Base</div>
                <div className="lv2-vp-tag lv2-vp-tag-right">Edit</div>
            </div>
        );
    }

    if (propKey === "scale") {
        return (
            <div key={animKey} className="lv2-vp-slide">
                <img className="lv2-vp-scale-tl"  src={IMG_SCALE_DYSON}   alt="Dyson product variant" />
                <img className="lv2-vp-scale-bl"  src={IMG_SCALE_DYSON}   alt="Dyson product variant" />
                <img className="lv2-vp-scale-r"   src={IMG_SCALE_DYSON4}  alt="4 Dyson product variants" />
                <img className="lv2-vp-scale-mid" src={IMG_SCALE_DYSON12} alt="12 Dyson product variants at scale" />
            </div>
        );
    }

    return null;
}

// ─── Component ────────────────────────────────────────────────────────────────
const ValuePropsSection = () => {
    const [activeIdx, setActiveIdx] = useState(null); // none expanded by default
    const [slideIdx,  setSlideIdx]  = useState(0);

    // Reset slide counter when switching props
    useEffect(() => {
        setSlideIdx(0);
    }, [activeIdx]);

    // Auto-advance slides within the active prop
    useEffect(() => {
        if (activeIdx === null) return;
        const count = PROPS[activeIdx].slideCount;
        if (count <= 1) return;
        const id = setInterval(() => {
            setSlideIdx(i => (i + 1) % count);
        }, 3500);
        return () => clearInterval(id);
    }, [activeIdx]);

    return (
        <div className="lv2-vp-wrapper">

            {/* ── Heading ──────────────────────────────────────────────────── */}
            <div className="lv2-vp-heading">
                <h2 className="manrope f-50 w-600 lh-60 lv2-vp-title">
                    Launch listings with confidence
                </h2>
                <p className="manrope f-20 w-500 lh-28 lv2-vp-subtitle">
                    High-quality, accurate visuals — with the control and scale to support every launch.
                </p>
            </div>

            {/* ── 4 value prop cards ───────────────────────────────────────── */}
            <div className={`lv2-vp-cards${activeIdx === null ? " lv2-vp-cards--default" : ` lv2-vp-cards--active-${activeIdx}`}`}>
                {PROPS.map((prop, i) => {
                    const isActive = activeIdx !== null && i === activeIdx;
                    return (
                        <div
                            key={prop.key}
                            className={`lv2-vp-prop-wrap${isActive ? " lv2-vp-prop-wrap--active" : ""}`}
                            onMouseLeave={() => setActiveIdx(null)}
                        >

                            {/* Card */}
                            <div
                                className={`lv2-vp-card ${isActive ? "lv2-vp-card--expanded" : "lv2-vp-card--compact"}`}
                                onMouseEnter={() => setActiveIdx(i)}
                            >
                                {isActive ? (
                                    <ExpandedSlide
                                        key={`${prop.key}-${slideIdx}`}
                                        propKey={prop.key}
                                        slideIdx={slideIdx}
                                    />
                                ) : (
                                    <>
                                        <img
                                            className={`lv2-vp-img ${prop.compactCls}`}
                                            src={prop.compactImg}
                                            alt={prop.label}
                                        />
                                        <div className="lv2-vp-card-fade" />
                                        <div className="lv2-vp-card-label">
                                            <span className="lv2-vp-label-text">{prop.label}</span>
                                            <img src={prop.icon} alt={prop.label} className="lv2-vp-icon" />
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Description below expanded card */}
                            {isActive && (
                                <p className="manrope f-16 w-500 lh-24 lv2-vp-description">
                                    {prop.description}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Mobile description panel (shown below cards on mobile) ──── */}
            {activeIdx !== null && (
                <p className="lv2-vp-mob-desc manrope f-14 w-500 lh-22 white">
                    {PROPS[activeIdx].description}
                </p>
            )}

            {/* ── AI models bar ────────────────────────────────────────────── */}
            <div className="lv2-vp-tools">
                <p className="lv2-vp-tools-heading">
                    Powered by leading<br />AI models in the industry
                </p>
                <div className="lv2-vp-logos-outer">
                    <div className="lv2-vp-logos-fade lv2-vp-logos-fade-left"  />
                    <div className="lv2-vp-logos-fade lv2-vp-logos-fade-right" />
                    <div className="lv2-vp-logos-track">
                        {LOGOS.map((logo, i) => (
                            <div key={i} className="lv2-vp-logo-item">
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    style={{ width: logo.w, height: logo.h, objectFit: "contain" }}
                                />
                                {logo.label && (
                                    <span className="lv2-vp-logo-label">{logo.label}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default ValuePropsSection;
